import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * How big a region tile's chrome should be, keyed on the RENDERED short side of the tile
 * the referenced element sits in: 1× on a postcard, up to 2.2× on half a television, and
 * down to 0.7× on the slivers a dense wall makes of a phone.
 *
 * IT SHRINKS NOW, AND IT RE-MEASURES. Both halves were wrong before and in the same
 * direction — the floor was 1× and the measurement happened once at mount:
 *
 *   • 1× is not small. On the Community Wall's floor a phone gets tiles about 130px
 *     across, where a 1× crown is a third of the picture. The chrome was competing with
 *     the clip it sits on instead of labelling it.
 *   • Mount is not the only moment. The comment here used to argue a tile "only changes
 *     size with a relayout, which remounts it" — true until the wall shipped an adaptive
 *     board, where an orientation flip re-hangs every tile WITHOUT remounting, and still
 *     untrue of the plainest thing a person does to a window: resize it.
 *
 * A ResizeObserver answers both, and fires once on observe so there is no separate
 * initial measurement. 0.7 is the floor because chrome smaller than that stops being a
 * target you can hit — a crown nobody can tap is worse than one that crowds its tile.
 *
 * One rule shared by the hover chrome and the fap crown, so the wall's controls scale
 * together instead of each inventing its own idea of a big tile.
 */
export function useTileScale(ref: RefObject<HTMLElement | null>): number {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const tile = ref.current?.parentElement;
    if (!tile) return;
    const ro = new ResizeObserver(() => {
      const short = Math.min(tile.clientWidth, tile.clientHeight);
      // A tile mid-teardown measures 0; leave the last good scale rather than snapping
      // the chrome to its floor for a frame.
      if (short > 0) setScale(Math.min(2.2, Math.max(0.7, short / 280)));
    });
    ro.observe(tile);
    return () => ro.disconnect();
  }, [ref]);
  return scale;
}
